import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { ORDER_STEPS } from '@ecp/common/src/const/order';
import { SiteNumberFunc } from '@ecp/common/src/const/site/siteNumberConst';

import { systemInfoFetchPendingState, systemInformationState } from '@fo-recoil/system/atom';
import {
  getIsMultimall,
  getLunchTime,
  getOperationTime,
  getIsEventMall,
  getIsMarketingUse,
  getDisplayPointName,
  getIsCartUse,
  getIsPriceOpen,
  getIsPointUse,
  getIsCouponMenuUse,
  getIsBulkOrdMenuUseYn,
  getSiteNumber,
  getIsNoMemberUseYn,
  getPriceFormat,
  getPriceFormatPerKrw,
} from '@fo-recoil/system/selector';
import commonApi from '@fo-apis/common/commonApi';

const useSiteInfo = () => {
  const [isPending, setIsPending] = useRecoilState(systemInfoFetchPendingState);
  const [systemInformation, setSystemInformation] = useRecoilState(systemInformationState);

  const getSystemInformationByKey = useCallback((key) => systemInformation[key], [systemInformation]);

  const operationTime = useRecoilValue(getOperationTime);
  const lunchTime = useRecoilValue(getLunchTime);
  const isMultimall = useRecoilValue(getIsMultimall);
  const isEventMall = useRecoilValue(getIsEventMall);
  const isMarketingUse = useRecoilValue(getIsMarketingUse);
  const displayPointName = useRecoilValue(getDisplayPointName);
  const isCartUse = useRecoilValue(getIsCartUse);
  const isPriceOpen = useRecoilValue(getIsPriceOpen);
  const isPointUse = useRecoilValue(getIsPointUse);
  const isCouponMenuUse = useRecoilValue(getIsCouponMenuUse);
  const isbulkOrdMenuUseYn = useRecoilValue(getIsBulkOrdMenuUseYn);
  const priceFormat = useRecoilValue(getPriceFormat); // 원 or 마일리지 등등 가격단위
  const priceFormatPerKrw = useRecoilValue(getPriceFormatPerKrw); // 가격단위 환산 비율 (1가격단위 = n원)
  const siteNumber = useRecoilValue(getSiteNumber);
  const isNoMemberUseYn = useRecoilValue(getIsNoMemberUseYn);

  async function fetch() {
    setIsPending(true);

    const { result } = await commonApi.getSiteInformation();
    setSystemInformation(result);

    setIsPending(false);

    return result;
  }

  /**
   * ECMJ 프로그램 아이디 : PRG_SBABY_01_01
   * ORDER_STEPS 의 name을 사이트 설정에 따라 변경한다.
   * 작업자 : 강기단
   */
  const useSiteOrderSteps = useMemo(() => {
    const siteOrderSteps = { ...ORDER_STEPS };

    siteOrderSteps.cart.name = SiteNumberFunc.cartName(siteNumber) ?? siteOrderSteps.cart.name;
    siteOrderSteps.payment.name = SiteNumberFunc.paymentName(siteNumber) ?? siteOrderSteps.payment.name;
    siteOrderSteps.complete.name = SiteNumberFunc.completeName(siteNumber) ?? siteOrderSteps.complete.name;
    if (!isCartUse) {
      delete siteOrderSteps.cart;
    }

    return siteOrderSteps;
  }, [isCartUse, siteNumber]);

  return {
    isPending,
    fetch,
    systemInformation,
    operationTime,
    lunchTime,
    getSystemInformationByKey,
    setSystemInformation,
    isMultimall,
    isEventMall,
    isMarketingUse,
    displayPointName,
    isCartUse,
    isPriceOpen,
    isPointUse,
    isCouponMenuUse,
    isbulkOrdMenuUseYn,
    siteNumber,
    isNoMemberUseYn,
    priceFormat,
    priceFormatPerKrw,
    useSiteOrderSteps,
  };
};

export default useSiteInfo;
